import React from 'react';
import {
  pathSetDisplaySeqAction,
  pathSetEventsPerStepAction,
} from '../../../../../redux/slices/analyses';
import styled from 'styled-components';
import {
  PathDirectionKey,
  PathModeKey,
  QueryType,
} from '../../../../../utils/apollo/graphqlSchema';
import { SvgIcon } from '../../../../Icon';
import { assertCondition } from '../../../../../utils/types';
import { useChartContext } from '../../../ChartContext';
import { useAppDispatch } from '../../../../../redux/hooks';
import KubitTooltip from '../../../../KubitTooltip';
import ControlButton from '../../../../Button/ControlButton';

const ControlContainer = styled.div`
  margin-right: auto;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  column-gap: 16px;
  row-gap: 4px;
`;

const ControlContainerButtonGroup = styled.div`
  display: flex;
  align-items: center;
`;

const DirectionInfoContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  column-gap: 4px;
`;

const DirectionText = styled.div`
  font-size: 12px;
  font-weight: 500;
`;

const ArrowLeft = styled(SvgIcon)`
  transform: rotate(180deg);
`;
const ArrowUp = styled(SvgIcon)`
  transform: rotate(-90deg);
`;
const ArrowDown = styled(SvgIcon)`
  transform: rotate(90deg);
`;

export default () => {
  const dispatch = useAppDispatch();
  const {
    metadata: { analysisId },
    chartData,
    settings: { chartSettings },
  } = useChartContext();
  assertCondition(chartData?.queryType === QueryType.Path);
  if (chartData.mode === PathModeKey.TopPaths) {
    return null;
  }

  const { displaySeq, pathEventsPerStep } = chartSettings;
  assertCondition(chartData.mode === PathModeKey.Sankey);
  const { maxSeq, directionKey } = chartData;

  const clampedDisplaySeq = Math.max(0, Math.min(displaySeq, maxSeq));
  const decSeqDisabled = clampedDisplaySeq - 1 < 1;
  const incSecDisabled = clampedDisplaySeq + 1 > maxSeq;
  const decEventsPerStepDisabled = pathEventsPerStep - 1 < 1;

  const setSeqHandler = (delta: 1 | -1) => {
    if ((delta < 0 && decSeqDisabled) || (delta > 0 && incSecDisabled)) {
      return;
    }
    dispatch(
      pathSetDisplaySeqAction({
        id: analysisId,
        pathProps: {
          displaySeq: clampedDisplaySeq + delta,
        },
      }),
    );
  };

  const generateDirectionText = (pathDirectionKey: PathDirectionKey) => {
    if (!pathDirectionKey) {
      return '';
    }

    if (pathDirectionKey === PathDirectionKey.Forward) {
      return (
        <DirectionInfoContainer>
          <DirectionText>Starting Event(s)</DirectionText>
          <SvgIcon
            name="arrow-right"
            width={12}
            height={12}
            customFill="currentColor"
          />
          <DirectionText>Ending Event(s)</DirectionText>
        </DirectionInfoContainer>
      );
    } else if (pathDirectionKey === PathDirectionKey.Backward) {
      return (
        <DirectionInfoContainer>
          <DirectionText>Ending Event(s)</DirectionText>
          <SvgIcon
            name="arrow-right"
            width={12}
            height={12}
            customFill="currentColor"
          />
          <DirectionText>Starting Event(s)</DirectionText>
        </DirectionInfoContainer>
      );
    }
  };

  const generatePopupMessage = (
    pathDirection: PathDirectionKey,
    buttonType: string,
  ) => {
    if (pathDirection === 'FORWARD') {
      return buttonType === 'REMOVE'
        ? 'Remove the last step'
        : 'Add one more step';
    } else if (pathDirection === 'BACKWARD') {
      return buttonType === 'REMOVE'
        ? 'Remove the first step'
        : 'Add one more step in the beginning';
    }
  };

  return (
    <ControlContainer>
      <ControlContainerButtonGroup>
        <KubitTooltip
          placement="bottom-end"
          disabled={decSeqDisabled}
          content={generatePopupMessage(directionKey, 'REMOVE')}
          noOpenDelay={true}
        >
          <ControlButton
            disabled={decSeqDisabled}
            onClick={() => setSeqHandler(-1)}
          >
            <ArrowLeft
              name="path-arrow-right"
              isActive={!decSeqDisabled}
              customFill="currentColor"
            />
          </ControlButton>
        </KubitTooltip>
        <KubitTooltip
          placement="bottom-end"
          disabled={incSecDisabled}
          content={generatePopupMessage(directionKey, 'ADD')}
          noOpenDelay={true}
        >
          <ControlButton
            disabled={incSecDisabled}
            onClick={() => setSeqHandler(1)}
          >
            <SvgIcon
              name="path-arrow-right"
              isActive={!incSecDisabled}
              customFill="currentColor"
            />
          </ControlButton>
        </KubitTooltip>
        <KubitTooltip
          placement="bottom-end"
          content={'Fewer events per step'}
          noOpenDelay={true}
        >
          <ControlButton
            disabled={decEventsPerStepDisabled}
            onClick={() =>
              dispatch(
                pathSetEventsPerStepAction({
                  id: analysisId,
                  pathProps: {
                    eventsPerStep: pathEventsPerStep - 1,
                  },
                }),
              )
            }
          >
            <ArrowUp
              name="path-arrow-right"
              isActive={!decEventsPerStepDisabled}
              customFill="currentColor"
            />
          </ControlButton>
        </KubitTooltip>
        <KubitTooltip
          placement="bottom-end"
          content={'More events per step'}
          noOpenDelay={true}
        >
          <ControlButton
            onClick={() =>
              dispatch(
                pathSetEventsPerStepAction({
                  id: analysisId,
                  pathProps: {
                    eventsPerStep: pathEventsPerStep + 1,
                  },
                }),
              )
            }
          >
            <ArrowDown
              name="path-arrow-right"
              isActive
              customFill="currentColor"
            />
          </ControlButton>
        </KubitTooltip>
      </ControlContainerButtonGroup>
      {generateDirectionText(directionKey)}
    </ControlContainer>
  );
};
